import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const HiddenMarketSection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Main Statement */}
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-[#0025cc] to-[#4F46E5]">
                <Icon name="TrendingUp" size={24} color="#FFFFFF" />
              </div>
              <span className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-[#0025cc] to-[#4F46E5] bg-clip-text text-transparent">
                {t('hiddenMarketSection.mainStat')}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#111827] mb-4">
              {t('hiddenMarketSection.mainTitle')}
            </h2>
            <p className="text-base md:text-lg text-[#6B7280] leading-relaxed">
              {t('hiddenMarketSection.mainSubtitle')}
            </p>
          </div>

          {/* FOR COMPANIES Section */}
          <div className="mb-12 md:mb-16">
            <div className="bg-gradient-to-br from-blue-50/50 to-indigo-50/30 rounded-3xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                {/* Left: Main Content */}
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-full mb-6">
                    <Icon name="Building2" size={16} color="#0025cc" />
                    <span className="text-sm font-semibold text-[#0025cc] uppercase tracking-wide">
                      {t('hiddenMarketSection.forCompanies')}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#111827] mb-4">
                    {t('hiddenMarketSection.companiesTitle')}
                  </h3>
                  
                  <p className="text-base md:text-lg text-[#6B7280] mb-8 leading-relaxed">
                    {t('hiddenMarketSection.companiesDesc')}
                  </p>
                  
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-md bg-green-100 flex items-center justify-center mt-0.5">
                        <Icon name="Check" size={16} color="#10B981" />
                      </div>
                      <span className="text-base text-[#374151]">
                        {t('hiddenMarketSection.companiesBenefit1')}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-md bg-green-100 flex items-center justify-center mt-0.5">
                        <Icon name="Check" size={16} color="#10B981" />
                      </div>
                      <span className="text-base text-[#374151]">
                        {t('hiddenMarketSection.companiesBenefit2')}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-md bg-green-100 flex items-center justify-center mt-0.5">
                        <Icon name="Check" size={16} color="#10B981" />
                      </div>
                      <span className="text-base text-[#374151]">
                        {t('hiddenMarketSection.companiesBenefit3')}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-md bg-green-100 flex items-center justify-center mt-0.5">
                        <Icon name="Check" size={16} color="#10B981" />
                      </div>
                      <span className="text-base text-[#374151]">
                        {t('hiddenMarketSection.companiesBenefit4')}
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Right: Benefits Card */}
                <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100">
                  <h4 className="text-lg md:text-xl font-bold text-[#111827] mb-6">
                    {t('hiddenMarketSection.whyDifferent')}
                  </h4>
                  
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                        <Icon name="Shield" size={20} color="#10B981" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-[#111827] mb-1">
                          {t('hiddenMarketSection.exclusiveNetworkTitle')}
                        </h5>
                        <p className="text-sm text-[#6B7280]">
                          {t('hiddenMarketSection.exclusiveNetworkDesc')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                        <Icon name="UserCheck" size={20} color="#10B981" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-[#111827] mb-1">
                          {t('hiddenMarketSection.rigorousVerification')}
                        </h5>
                        <p className="text-sm text-[#6B7280]">
                          {t('hiddenMarketSection.rigorousVerificationDesc')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                        <Icon name="Zap" size={20} color="#10B981" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-[#111827] mb-1">
                          {t('hiddenMarketSection.directPassiveTalent')}
                        </h5>
                        <p className="text-sm text-[#6B7280]">
                          {t('hiddenMarketSection.directPassiveTalentDesc')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* View Senior Professionals Button */}
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <Link
                      to="/executive-preview-gallery"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#0025cc] to-[#4F46E5] text-white font-semibold rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
                    >
                      <Icon name="Users" size={20} color="#FFFFFF" />
                      <span>{t('hiddenMarketSection.viewSeniorProfessionals')}</span>
                      <Icon name="ArrowRight" size={18} color="#FFFFFF" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FOR CANDIDATES Section */}
          <div>
            <div className="bg-gradient-to-br from-pink-50/50 to-rose-50/30 rounded-3xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                {/* Left: Benefits Card */}
                <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100">
                  <h4 className="text-lg md:text-xl font-bold text-[#111827] mb-6">
                    {t('hiddenMarketSection.yourAdvantages')}
                  </h4>
                  
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-pink-50 flex items-center justify-center">
                        <Icon name="Shield" size={20} color="#EC4899" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-[#111827] mb-1">
                          {t('hiddenMarketSection.anonymousExploration')}
                        </h5>
                        <p className="text-sm text-[#6B7280]">
                          {t('hiddenMarketSection.anonymousExplorationDesc')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-pink-50 flex items-center justify-center">
                        <Icon name="Unlock" size={20} color="#EC4899" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-[#111827] mb-1">
                          {t('hiddenMarketSection.accessHiddenMarket')}
                        </h5>
                        <p className="text-sm text-[#6B7280]">
                          {t('hiddenMarketSection.accessHiddenMarketDesc')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-pink-50 flex items-center justify-center">
                        <Icon name="Shield" size={20} color="#EC4899" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-[#111827] mb-1">
                          {t('hiddenMarketSection.completeConfidentiality')}
                        </h5>
                        <p className="text-sm text-[#6B7280]">
                          {t('hiddenMarketSection.completeConfidentialityDesc')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Main Content */}
                <div className="lg:order-first">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-pink-100 rounded-full mb-6">
                    <Icon name="Heart" size={16} color="#EC4899" />
                    <span className="text-sm font-semibold text-[#EC4899] uppercase tracking-wide">
                      {t('hiddenMarketSection.forCandidates')}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#111827] mb-4">
                    {t('hiddenMarketSection.candidatesTitle')}
                  </h3>
                  
                  <p className="text-base md:text-lg text-[#6B7280] mb-8 leading-relaxed">
                    {t('hiddenMarketSection.candidatesDesc')}
                  </p>
                  
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-md bg-green-100 flex items-center justify-center mt-0.5">
                        <Icon name="Check" size={16} color="#10B981" />
                      </div>
                      <span className="text-base text-[#374151]">
                        {t('hiddenMarketSection.candidatesBenefit1')}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-md bg-green-100 flex items-center justify-center mt-0.5">
                        <Icon name="Check" size={16} color="#10B981" />
                      </div>
                      <span className="text-base text-[#374151]">
                        {t('hiddenMarketSection.candidatesBenefit2')}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-md bg-green-100 flex items-center justify-center mt-0.5">
                        <Icon name="Check" size={16} color="#10B981" />
                      </div>
                      <span className="text-base text-[#374151]">
                        {t('hiddenMarketSection.candidatesBenefit3')}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HiddenMarketSection;